<template>
  <v-content
    v-if="application && opportunity && opportunity.organization"
    class="candidate application page"
  >
    <OpportunityCoverImage :opportunity="opportunity" height="30vh" />

    <v-container style="max-width: 900px;" class="pt-0">
      <v-card
        :class="{ 'card--overlap': opportunity.coverImage || opportunity.organization.coverImage }"
      >
        <v-layout column>
          <v-flex d-flex class="text-xs-center card__logo--overlap">
            <div>
              <v-avatar :size="avatarSize + 2" :color="themes.candidate.light.muted">
                <v-avatar :size="avatarSize">
                  <img :src="opportunity.organization.logo" alt="avatar" />
                </v-avatar>
              </v-avatar>
            </div>
          </v-flex>

          <div d-flex class="text-xs-right">
            <v-menu left x-offset>
              <v-btn slot="activator" fab flat :color="themes.candidate.light.muted">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
              <v-card>
                <v-list>
                  <LocationSelect :value="application">
                    <v-list-tile
                      slot="activator"
                      slot-scope="{ toggle }"
                      v-if="isActive"
                      @click="toggle"
                      class="px-0"
                      avatar
                    >
                      <v-list-tile-avatar>
                        <v-icon>mdi-map-marker</v-icon>
                      </v-list-tile-avatar>
                      <v-list-tile-content>
                        <v-list-tile-title>Update locations</v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </LocationSelect>
                  <v-list-tile
                    v-if="isActive"
                    @click="openChat({ opportunityName: application.opportunity.name, organizationName: application.opportunity.organization.name })"
                    :color="themes.candidate.light.danger"
                    class="px-0"
                    avatar
                  >
                    <v-list-tile-avatar>
                      <v-icon :color="themes.candidate.light.danger">mdi-delete</v-icon>
                    </v-list-tile-avatar>
                    <v-list-tile-content>
                      <v-list-tile-title>Withdraw application</v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>
              </v-card>
            </v-menu>
          </div>

          <v-flex d-flex class="text-xs-center mt-5">
            <v-card-text>
              <div>
                <div class="headline">
                  <router-link :to="`/opportunities/${opportunity.id}`" class="black--text">
                    {{ opportunity.name }}
                  </router-link>
                </div>
                <div class="pt-1 subheading">
                  <a
                    @click="$router.push(`/organizations/${opportunity.organization.id}`)"
                    :class="themes.candidate.light.mutedText"
                  >
                    {{ opportunity.organization.name }}
                  </a>
                </div>
              </div>
            </v-card-text>
          </v-flex>

          <v-flex d-flex class="text-xs-center">
            <!-- Quick overview -->
            <v-card-text>
              <v-layout row wrap align-center justify-space-around>
                <div v-if="application.locations && application.locations.length" class="px-1">
                  <v-menu bottom offset-y open-on-hover>
                    <v-chip slot="activator" outline class="transparent">
                      <v-icon left class="text-vertical-middle">mdi-map-marker</v-icon>
                      {{ application.locations[0].address }}&nbsp;
                      <span
                        v-if="application.locations.length > 1"
                        :class="themes.candidate.light.mutedText"
                        small
                      >
                        ...+ {{ application.locations.length - 1 }}
                      </span>
                    </v-chip>
                    <v-card dark>
                      <v-card-text>
                        Your application is valid for the following location(s)
                      </v-card-text>
                      <v-list>
                        <v-list-tile
                          avatar
                          v-for="location in application.locations"
                          :key="`location-${location.id}`"
                          class="px-0"
                        >
                          <v-list-tile-avatar> <v-icon>mdi-map-marker</v-icon> </v-list-tile-avatar>
                          <v-list-tile-content>
                            <v-list-tile-title>{{ location.address }}</v-list-tile-title>
                          </v-list-tile-content>
                        </v-list-tile>
                      </v-list>
                    </v-card>
                  </v-menu>
                </div>
                <div v-if="opportunity.totalIntake" class="px-1">
                  <v-menu bottom open-on-hover offset-y>
                    <v-chip slot="activator" outline class="transparent">
                      <v-icon left class="text-vertical-middle">mdi-account-multiple-plus</v-icon>
                      {{ opportunity.totalIntake }}&nbsp;positions
                    </v-chip>
                    <v-card dark>
                      <v-card-text>
                        This opportunity has {{ opportunity.totalIntake }} vacancies
                      </v-card-text>
                    </v-card>
                    <span></span>
                  </v-menu>
                </div>
                <div v-if="opportunity.commencesAt" class="px-1">
                  <v-menu bottom open-on-hover offset-y>
                    <v-chip slot="activator" outline class="transparent">
                      <v-icon left class="text-vertical-middle">mdi-calendar</v-icon>
                      {{ new Date(application.opportunity.commencesAt) | date('d MMMM yyy') }}
                    </v-chip>
                    <v-card dark>
                      <v-card-text>
                        This opportunity commences on the
                        {{ new Date(application.opportunity.commencesAt) | date('do MMMM yyy') }}
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </div>
              </v-layout>
            </v-card-text>
          </v-flex>

          <!-- Phase Stepper -->
          <v-flex d-flex class="text-xs-center mx-5">
            <v-card-text>
              <PhaseStepper :application="application" />
            </v-card-text>
          </v-flex>
        </v-layout>
      </v-card>

      <!-- Withdraw application button -->
      <!-- <v-dialog lazy absolute -->
      <!--   v-model="withdrawDialog" -->
      <!--   max-width="40%" -->
      <!--   :class="$vuetify.breakpoint.xsOnly ? 'mt-2' : 'mt-0'" -->
      <!-- > -->
      <!--   <v-btn flat class="red--text" slot="activator"> Withdraw Application </v-btn> -->
      <!--   <v-card> -->
      <!--     <v-card-title> <div class="headline">Withdraw</div> </v-card-title> -->
      <!--     <v-card-text>Are you sure?</v-card-text> -->
      <!--     <v-card-actions class="grey lighten-4"> -->
      <!--       <v-spacer /> -->
      <!--       <v-btn class="grey--text" flat @click.native="withdrawDialog = false">Cancel</v-btn> -->
      <!--       <v-btn flat -->
      <!--         class="red--text" -->
      <!--         @click="withdraw(application)" -->
      <!--         :loading="application.loading" -->
      <!--         > -->
      <!--           Withdraw -->
      <!--       </v-btn> -->
      <!--     </v-card-actions> -->
      <!--   </v-card> -->
      <!-- </v-dialog> -->
      <!-- </v-card-actions> -->
      <!-- </v-card> -->

      <!-- Household Income Assessment -->
      <FinancialAssessment
        v-if="application.financial"
        :financial="application.financial"
        @refresh="refreshFinancial"
      />

      <!-- Questionnaires -->
      <v-card class="pa-4 mt-4" width="100%" v-if="questionnaires && questionnaires[0]">
        <v-card-title class="title pb-1"> Screening Questions </v-card-title>
        <Questionnaire
          v-for="questionnaire in questionnaires"
          :key="`questionnaire-${questionnaire.id}`"
          :questionnaire="questionnaire"
        />
      </v-card>
    </v-container>
  </v-content>
</template>

<script>
// Mixins
import { managedDocumentAttributes } from '@/mixins/managedDocumentAttributes'

// Components
import FinancialAssessment from '@/components/candidate/applications/FinancialAssessment'
import LocationSelect from '@/components/candidate/applications/LocationSelect'
import OpportunityCoverImage from '@/components/guest/opportunities/OpportunityCoverImage'
import PhaseStepper from '@/components/candidate/applications/PhaseStepper'
import Questionnaire from '@/components/candidate/applications/Questionnaire'

// Helpers
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'

// Vue Component
export default {
  mixins: [managedDocumentAttributes],

  components: {
    FinancialAssessment,
    LocationSelect,
    OpportunityCoverImage,
    PhaseStepper,
    Questionnaire,
  },
  created() {
    this.$store
      .dispatch('candidate/applications/fetch', this.$route.params.id)
      .then(this.setDocumentAttributes)
    this.$store.dispatch('candidate/profile/fetch')
  },
  data() {
    return {
      withdrawDialog: false,
    }
  },
  computed: {
    ...mapGetters({ profile: 'candidate/profile/model' }),

    application: () => Vue.$store.getters['candidate/applications/find'](Vue.$route.params.id),

    avatarSize() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return 65
        case 'sm':
          return 85
        default:
          return 162
      }
    },

    isActive() {
      return this.application.status !== 'Withdrawn' && this.application.status !== 'Unsuccessful'
    },

    opportunity() {
      if (!this.application.opportunity) return

      return this.application.opportunity
    },

    questionnaires() {
      if (this.application && this.application.opportunity && this.application.opportunity.phases) {
        const phases = this.application.opportunity.phases.filter(phase => phase.behaviors)
        const sets = []
        phases.forEach(phase => {
          phase.behaviors.forEach(behavior => {
            if (behavior.type === 'OpportunityPhaseBehavior::Questionnaire') {
              sets.push(Object.assign({}, behavior, { phase: { id: phase.id, name: phase.name } }))
            }
          })
        })
        return sets
      } else {
        return []
      }
    },
    orderedPhases() {
      if (
        _.get(this.application, 'opportunity.phases[0]') &&
        this.application.opportunity.phases.find(p => p.initial)
      ) {
        let order = 1
        let current = this.application.opportunity.phases.find(p => p.initial)
        let phases = [{ id: current.id, name: current.name, order }]
        while (current.next) {
          current = this.application.opportunity.phases.find(p => p.name === current.next.name)
          order++
          phases.push({ id: current.id, name: current.name, order })
        }

        return phases
      }

      return []
    },
    currentPhase() {
      if (this.application.status !== 'Withdrawn' && this.application.status !== 'Unsuccessful') {
        return this.orderedPhases.find(p => p.name === this.application.status)
      }
      return this.application.status
    },
  },

  methods: {
    setDocumentAttributes() {
      if (_.get(this, 'application.opportunity.name')) {
        this.documentAttributes.title = `${this.application.opportunity.name} Application – Leaply`
      } else {
        setTimeout(this.setDocumentAttributes, 100)
      }
    },
    refreshFinancial() {
      if (!this.application) return

      this.$http.get('/candidate/financial_survey').then(response => {
        this.application.financial = response.data
      })
    },

    // mapActions({ withdraw: 'candidate/applications/withdraw' }),
    openChat({ opportunityName, organizationName }) {
      if (window.CRISP_READY && window.$crisp.is('chat:opened')) return

      window.$crisp.push(['do', 'chat:open'])
      window.$crisp.push([
        'set',
        'message:text',
        [
          opportunityName
            ? `Hi! I would like to withdraw my application from ${opportunityName} at ${organizationName}. Can you help me?`
            : 'Hi! I would like to withdraw one of my applications. Can you help me?',
        ],
      ])
    },
  },
}
</script>

<style lang="scss">
.candidate.application.page {
  .card--overlap {
    margin-top: 0;

    @media (min-width: 960px) {
      margin-top: -3.9rem;
    }
  }

  .card__logo--overlap {
    position: absolute;
    transform: translate(-50%, -50%);
    left: 50%;

    @media (min-width: 960px) {
      transform: translate(-50%, -70%);
    }
  }
}
</style>
